import { ISimpleTableCellRenderProps, ISimpleTableField, SimpleTable } from "@asup/simple-table";
import { EditorV3, EditorV3Content, IEditorV3 } from "@azu-business/editor-v3";
import { AitTableData } from "aic";
import { AnnotationOptionType } from "components/annotations/interface";
import { stylesToV3 } from "components/common/stylesToV3";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { TlfContext } from "contexts/TlfContext";
import { eStyleTlfType } from "functions/api/styles/getAllStyles";
import { useContext, useMemo } from "react";
import { Container } from "react-bootstrap";
import "./NotesTable.css";

interface NotesLookupProps extends Record<
  string,
  string | AnnotationOptionType | IEditorV3 | undefined
> {
  optionType: AnnotationOptionType;
  notesContent: EditorV3Content;
  notesAt: string;
}

/**
 * Inline editor wrapper used by the notes table to render V3 editor content.
 *
 * @param props - Table cell render props containing `rowData` and indexing info.
 */
const EditorV3SimpleTable = (props: ISimpleTableCellRenderProps): React.ReactElement => {
  const appSettings = useContext(AppSettingsContext);
  return (
    <EditorV3
      id={`notes-cell-${props.rowNumber}-${props.columnNumber}`}
      input={props.rowData.notesContent as EditorV3Content}
      noBorder
      customStyleMap={stylesToV3(appSettings.styles, eStyleTlfType.Template)}
      setObject={
        // istanbul ignore next
        () => {}
      }
      editable={false}
    />
  );
};

EditorV3SimpleTable.displayName = "EditorV3SimpleTable";

const headers: ISimpleTableField[] = [
  {
    name: "optionType",
    label: "Option Type",
    width: "180px",
  },
  {
    name: "notesText",
    label: "Notes Text",
    width: "600px",
    renderFn: EditorV3SimpleTable,
  },
  {
    name: "notesAt",
    label: "Notes at",
    width: "300px",
    /**
     * Renders the 'Notes at' cell for the notes table, showing a location string.
     *
     * @param props - Table cell render props containing `rowData`.
     */
    renderFn: (props: ISimpleTableCellRenderProps): React.ReactElement => (
      <div style={{ textAlign: "center", width: "100%" }}>{props.rowData.notesAt as string}</div>
    ),
  },
];

/**
 * Displays legacy annotations and notes extracted from the current TLF data.
 */
export const NotesTable = (): React.ReactElement => {
  const tlfContext = useContext(TlfContext);

  const notes = useMemo(() => {
    const currentNotes: {
      optionType: AnnotationOptionType;
      notesContent?: IEditorV3;
      notesAt: string;
    }[] = [];

    if (
      tlfContext &&
      tlfContext.tlfData &&
      "body" in tlfContext.tlfData &&
      tlfContext.tlfData.type !== "V1Figure"
    ) {
      const tableData = tlfContext.tlfData.body as AitTableData<IEditorV3>;
      currentNotes.push({
        optionType: "Table",
        notesContent: tableData.comments,
        notesAt: "Table",
      });
      /* istanbul ignore else */
      if (tableData.headerData) {
        currentNotes.push({
          optionType: "Header",
          notesContent: tableData.headerData.comments,
          notesAt: "Header",
        });
        tableData.headerData.rows.forEach((row, ri) => {
          row.cells.forEach((cell, ci) => {
            /* istanbul ignore else */
            if (cell.comments)
              currentNotes.push({
                optionType: "Cell",
                notesContent: cell.comments,
                notesAt: `Header, Row-${ri + 1}, Cell-${ci + 1}`,
              });
          });
        });
      }
      tableData.bodyData?.forEach((rowGroup, rgi) => {
        currentNotes.push({
          optionType: "RowGroup",
          notesContent: rowGroup.comments,
          notesAt: `Body, RowGroup-${rgi + 1}`,
        });

        rowGroup.rows.forEach((row, ri) => {
          row.cells.forEach((cell, ci) => {
            /* istanbul ignore else */
            if (cell.comments)
              currentNotes.push({
                optionType: "Cell",
                notesContent: cell.comments,
                notesAt: `Body, RowGroup-${rgi + 1}, Row-${ri + 1}, Cell-${ci + 1}`,
              });
          });
        });
      });
    }
    const transposed: NotesLookupProps[] = currentNotes.map((a) => ({
      ...a,
      notesContent: new EditorV3Content(a.notesContent),
    }));
    const filtered = transposed.filter((notes) => notes.notesContent.text.trim() !== "");
    return filtered;
  }, [tlfContext]);

  return (
    <>
      <Container className="mb-4 annotations-table-holder">
        {!notes || notes.length === 0 ? (
          <>No legacy annotations or notes.</>
        ) : (
          <SimpleTable
            id="notes-table"
            fields={headers}
            keyField="notesAt"
            data={notes}
            headerLabel="Legacy annotations and notes"
            showPager={false}
            tableClassName="notes-table"
          />
        )}
      </Container>
    </>
  );
};

NotesTable.displayName = "NotesTable";
